import React, { useState, useEffect } from 'react';
import { Occurrence, OccurrenceStatus, DashboardStats } from './types';
import { occurrenceService } from './occurrenceService';
import { OccurrencesTable } from './components/OccurrencesTable';
import { OccurrenceDetailModal } from './components/OccurrenceDetailModal';
import { CreateOccurrenceModal } from './components/CreateOccurrenceModal';
import { OccurrenceTypesTab } from './components/OccurrenceTypesTab';
import { isRoleContentRestricted } from '../../../shared/rolePermissions';

type SubmenuView = 'all' | 'open' | 'investigation' | 'closed' | 'types';

export const OccurrencesPage: React.FC = () => {
  const [currentView, setCurrentView] = useState<SubmenuView>('all');
  const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('');
  const [selectedOccurrence, setSelectedOccurrence] = useState<Occurrence | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const currentUserRole = (() => {
    try {
      const u = localStorage.getItem('auth_user');
      return u ? JSON.parse(u).user_type : '';
    } catch {
      return '';
    }
  })();
  const isRestrictedScope = isRoleContentRestricted(currentUserRole);

  const fetchStats = async () => {
    try {
      const data = await occurrenceService.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    }
  };

  const fetchOccurrences = async () => {
    if (currentView === 'types') return;

    try {
      setLoading(true);
      let statusFilter: OccurrenceStatus[] | undefined = undefined;

      if (currentView === 'open') {
        statusFilter = ['NEW', 'UNDER_REVIEW', 'VERIFIED', 'INVESTIGATION_REQUIRED', 'UNDER_INVESTIGATION', 'PENDING_ACTIONS', 'PENDING_CLOSURE', 'REOPENED'];
      } else if (currentView === 'investigation') {
        statusFilter = ['INVESTIGATION_REQUIRED', 'UNDER_INVESTIGATION'];
      } else if (currentView === 'closed') {
        statusFilter = ['CLOSED'];
      }

      const res = await occurrenceService.getOccurrences({
        status: statusFilter,
        severity: selectedSeverity ? (selectedSeverity as any) : undefined,
        searchTerm: searchTerm.trim() || undefined,
      });

      setOccurrences(res.items);
      setTotalCount(res.total);
    } catch (err: any) {
      console.error('Failed to load occurrences:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const handleUrlSelect = () => {
      const params = new URLSearchParams(window.location.search);
      const targetId = params.get('occurrenceId') || params.get('id');
      const targetNumber = params.get('occurrenceNumber') || params.get('ocrNumber');

      if (targetId) {
        occurrenceService
          .getOccurrenceById(targetId)
          .then((occ) => {
            if (occ) {
              setSelectedOccurrence(occ);
            } else if (targetNumber) {
              occurrenceService.getOccurrences({ searchTerm: targetNumber }).then((res) => {
                const found = res.items.find((o) => o.occurrenceNumber === targetNumber) || res.items[0];
                if (found) setSelectedOccurrence(found);
              }).catch(() => {});
            }
          })
          .catch((err) => {
            console.warn('Could not open occurrence by ID from URL:', err);
            if (targetNumber) {
              occurrenceService.getOccurrences({ searchTerm: targetNumber }).then((res) => {
                const found = res.items.find((o) => o.occurrenceNumber === targetNumber) || res.items[0];
                if (found) setSelectedOccurrence(found);
              }).catch(() => {});
            }
          });
      } else if (targetNumber) {
        occurrenceService
          .getOccurrenceById(targetNumber)
          .then((occ) => {
            if (occ) {
              setSelectedOccurrence(occ);
            } else {
              occurrenceService.getOccurrences({ searchTerm: targetNumber }).then((res) => {
                const found = res.items.find((o) => o.occurrenceNumber === targetNumber) || res.items[0];
                if (found) setSelectedOccurrence(found);
              }).catch(() => {});
            }
          })
          .catch(() => {
            occurrenceService.getOccurrences({ searchTerm: targetNumber }).then((res) => {
              const found = res.items.find((o) => o.occurrenceNumber === targetNumber) || res.items[0];
              if (found) setSelectedOccurrence(found);
            }).catch(() => {});
          });
      }
    };

    handleUrlSelect();
    window.addEventListener('popstate', handleUrlSelect);
    return () => window.removeEventListener('popstate', handleUrlSelect);
  }, []);

  useEffect(() => {
    fetchOccurrences();
  }, [currentView, selectedSeverity]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchOccurrences();
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Top Header */}
      <div className="page-header-banner">
        <div className="badge">SAFETY MANAGEMENT</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Occurrences Management</h1>
            <p>Verified operational safety events, ICAO ADREP / EASA ECCAIRS 2 taxonomy, and 72h MOR governance.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 18px' }}
          >
            + New Occurrence
          </button>
        </div>
      </div>

      {isRestrictedScope && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          marginBottom: '20px',
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          color: '#1e40af',
          fontSize: '13px',
          lineHeight: '1.4'
        }}>
          <span style={{ fontSize: '18px' }}>🔒</span>
          <div>
            <strong>Personal Record Scope:</strong> Operational crew view is active ({currentUserRole || 'Frontline'}). You are viewing occurrences authored by you, directly assigned to you, or where you participate as an investigator in accordance with operational safety data segregation.
          </div>
        </div>
      )}

      {/* KPI Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Occurrences</div>
            <div className="stat-value">{stats.totalOccurrences ?? '-'}</div>
            <div className="stat-sub">Logged events</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Open / Active</div>
            <div className="stat-value" style={{ color: '#0284c7' }}>{stats.openCount ?? '-'}</div>
            <div className="stat-sub">Active workflow</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Under Investigation</div>
            <div className="stat-value" style={{ color: '#9333ea' }}>{stats.underInvestigationCount ?? '-'}</div>
            <div className="stat-sub">Active analysis</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Closed Cases</div>
            <div className="stat-value" style={{ color: '#16a34a' }}>{stats.closedCount ?? '-'}</div>
            <div className="stat-sub">Resolved events</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">72h MOR Alerts</div>
            <div className="stat-value" style={{ color: '#dc2626' }}>{stats.urgentDeadlinesCount ?? 0}</div>
            <div className="stat-sub">Mandatory reporting</div>
          </div>
        </div>
      )}

      {/* Submenu Navigation */}
      <div className="nav-tabs">
        {[
          { id: 'all', label: 'All Occurrences' },
          { id: 'open', label: 'Open Occurrences' },
          { id: 'investigation', label: 'Under Investigation' },
          { id: 'closed', label: 'Closed Occurrences' },
          { id: 'types', label: 'Occurrence Types & Taxonomy' },
        ].map((sub) => (
          <button
            key={sub.id}
            onClick={() => setCurrentView(sub.id as SubmenuView)}
            className={`tab-btn ${currentView === sub.id ? 'active' : ''}`}
          >
            {sub.label}
          </button>
        ))}
      </div>

      {/* Filter and Search Bar (for occurrence list views) */}
      {currentView !== 'types' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px', flex: 1, maxWidth: '480px' }}>
            <input
              type="text"
              placeholder="Search by number, flight, aircraft, or title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
            />
            <button
              type="submit"
              style={{ background: '#334155', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
            >
              Search
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Severity:</label>
            <select
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
            >
              <option value="">All Severities</option>
              <option value="CRITICAL">CRITICAL</option>
              <option value="HIGH">HIGH</option>
              <option value="NORMAL">NORMAL</option>
              <option value="LOW">LOW</option>
            </select>
          </div>
        </div>
      )}

      {/* View Content */}
      {currentView === 'types' ? (
        <OccurrenceTypesTab />
      ) : (
        <OccurrencesTable
          occurrences={occurrences}
          loading={loading}
          onSelect={(occ) => setSelectedOccurrence(occ)}
          onRefresh={() => {
            fetchOccurrences();
            fetchStats();
          }}
        />
      )}

      {/* Modals */}
      {selectedOccurrence && (
        <OccurrenceDetailModal
          occurrenceId={selectedOccurrence.id}
          onClose={() => {
            setSelectedOccurrence(null);
            const params = new URLSearchParams(window.location.search);
            if (params.has('occurrenceId') || params.has('occurrenceNumber') || params.has('ocrNumber')) {
              params.delete('occurrenceId');
              params.delete('occurrenceNumber');
              params.delete('ocrNumber');
              const newUrl = `${window.location.pathname}?${params.toString()}`;
              window.history.replaceState({}, '', newUrl);
            }
          }}
          onUpdated={() => {
            fetchOccurrences();
            fetchStats();
          }}
        />
      )}

      {showCreateModal && (
        <CreateOccurrenceModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchOccurrences();
            fetchStats();
          }}
        />
      )}
    </div>
  );
};
